import React, { useEffect, useMemo, useRef } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  Circle,
  ZoomControl,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import { MapPin } from 'lucide-react';
import { renderToStaticMarkup } from 'react-dom/server';
import { cn } from '../../lib/utils';
import { colors } from '../../config/colors';

const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = defaultIcon;

const CARTO_LIGHT =
  'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
const CARTO_DARK =
  'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
const CARTO_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

const divIconCache = {};
function escapeMarkerHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function createDivIcon(color, size = 28) {
  const cacheKey = `${color}:${size}`;
  if (divIconCache[cacheKey]) {
    return divIconCache[cacheKey];
  }

  const pinMarkup = renderToStaticMarkup(
    <MapPin className="h-3.5 w-3.5" aria-hidden style={{ color: colors.text.inverse }} />
  );
  const html = `<div class="tenant-map-marker-pin" style="width:${size}px;height:${size}px;background-color:${color};border:2px solid ${colors.background.elevated};border-radius:9999px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.35);">${pinMarkup}</div>`;

  const icon = L.divIcon({
    html,
    className: 'tenant-map-marker-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
  divIconCache[cacheKey] = icon;
  return icon;
}

function createLabeledMapIcon({
  color,
  label,
  selected = false,
  variant = 'default',
}) {
  const safeLabel = escapeMarkerHtml(label || '');

  const dotSize = selected ? 34 : variant === 'own' ? 30 : 24;
  const glow = selected
    ? `box-shadow:0 0 0 4px ${colors.secondary[400]}66,0 0 18px ${colors.secondary[500]}99;`
    : 'box-shadow:0 2px 10px rgba(0,0,0,0.45);';
  const labelBg = selected ? colors.secondary[600] : colors.background.elevated;
  const labelColor = selected ? colors.text.inverse : colors.text.primary;
  const labelBorder = selected ? colors.secondary[500] : colors.border.light;

  const html = `<div class="tenant-map-labeled-marker" style="display:flex;flex-direction:column;align-items:center;gap:5px;pointer-events:auto;">
    <div style="width:${dotSize}px;height:${dotSize}px;background-color:${color};border:2px solid ${colors.background.elevated};border-radius:9999px;${glow}"></div>
    <div style="background:${labelBg};color:${labelColor};font-size:11px;font-weight:600;line-height:1.2;padding:3px 10px;border-radius:9999px;white-space:nowrap;max-width:148px;overflow:hidden;text-overflow:ellipsis;border:1px solid ${labelBorder};">${safeLabel}</div>
  </div>`;

  return L.divIcon({
    html,
    className: 'tenant-map-labeled-icon',
    iconSize: [148, dotSize + 28],
    iconAnchor: [74, dotSize / 2],
    popupAnchor: [0, -dotSize / 2],
  });
}

function Map({ center, zoom = 3, className, style, children, ...props }) {
  return (
    <div
      className={cn('relative z-0 h-full w-full overflow-hidden rounded-xl', className)}
      style={style}
    >
      <MapContainer
        center={center}
        zoom={zoom}
        zoomControl={false}
        className="h-full w-full"
        style={{ height: '100%', width: '100%' }}
        {...props}
      >
        {children}
      </MapContainer>
    </div>
  );
}

function MapTileLayer({ variant = 'dark', url, attribution = CARTO_ATTRIBUTION, ...props }) {
  const tileUrl = url ?? (variant === 'light' ? CARTO_LIGHT : CARTO_DARK);
  return <TileLayer url={tileUrl} attribution={attribution} {...props} />;
}

function MapZoomControl({ position = 'topleft' }) {
  return <ZoomControl position={position} />;
}

function MapMarker({ position, iconColor, icon, children, eventHandlers, ...props }) {
  const leafletIcon = useMemo(
    () => icon || (iconColor ? createDivIcon(iconColor) : defaultIcon),
    [icon, iconColor]
  );
  const normalizedPosition = useMemo(() => {
    if (!Array.isArray(position) || position.length < 2) return position;
    return [Number(position[0]), Number(position[1])];
  }, [position]);

  return (
    <Marker
      position={normalizedPosition}
      icon={leafletIcon}
      eventHandlers={eventHandlers}
      {...props}
    >
      {children}
    </Marker>
  );
}

function MapPopup({ children, className, ...props }) {
  return (
    <Popup className={cn('tenant-map-popup text-sm', className)} {...props}>
      {children}
    </Popup>
  );
}

function MapPolyline({
  positions,
  color = colors.brand[500],
  weight = 3,
  opacity = 0.85,
  dashed = false,
  ...props
}) {
  return (
    <Polyline
      positions={positions}
      pathOptions={{
        color,
        weight,
        opacity,
        ...(dashed ? { dashArray: '10, 10' } : {}),
      }}
      {...props}
    />
  );
}

function MapCircle({ center, radius = 80000, color = colors.info[500], fillOpacity = 0.12, ...props }) {
  return (
    <Circle
      center={center}
      radius={radius}
      pathOptions={{
        color,
        fillColor: color,
        fillOpacity,
        weight: 1.5,
        opacity: 0.55,
      }}
      {...props}
    />
  );
}

function MapFitBounds({ bounds, padding = [48, 48], onReady }) {
  const map = useMap();
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const boundsKey = useMemo(
    () => (Array.isArray(bounds) ? bounds.map((point) => point.join(',')).join('|') : ''),
    [bounds]
  );

  useEffect(() => {
    if (!bounds || bounds.length === 0) {
      onReadyRef.current?.(false);
      return undefined;
    }

    const applyFit = () => {
      try {
        const latLngs = bounds
          .filter((point) => Array.isArray(point) && point.length >= 2)
          .map((point) => L.latLng(Number(point[0]), Number(point[1])));
        if (latLngs.length === 0) {
          onReadyRef.current?.(false);
          return;
        }
        const leafletBounds =
          latLngs.length === 1
            ? L.latLngBounds(latLngs[0], latLngs[0])
            : L.latLngBounds(latLngs);

        map.invalidateSize({ animate: false });
        map.fitBounds(leafletBounds, { padding, maxZoom: 12 });
        window.requestAnimationFrame(() => {
          map.invalidateSize({ animate: false });
          onReadyRef.current?.(true);
        });
      } catch (_) {
        onReadyRef.current?.(false);
      }
    };

    const timer = setTimeout(applyFit, 80);
    return () => clearTimeout(timer);
  }, [map, bounds, boundsKey, padding]);

  return null;
}

/** Recompute tile layout after mount or when container size changes. */
function MapInvalidateSize({ invalidateKey = 0 }) {
  const map = useMap();
  useEffect(() => {
    const syncMap = () => {
      map.invalidateSize({ animate: false });
      map.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          layer.update();
        }
      });
    };

    const timer = setTimeout(syncMap, 100);
    const timer2 = setTimeout(syncMap, 350);
    const onResize = () => syncMap();
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
      window.removeEventListener('resize', onResize);
    };
  }, [map, invalidateKey]);
  return null;
}

export {
  Map,
  MapTileLayer,
  MapZoomControl,
  MapMarker,
  MapPopup,
  MapPolyline,
  MapCircle,
  MapFitBounds,
  MapInvalidateSize,
  createDivIcon,
  createLabeledMapIcon,
};
